import React, { RefCallback, useEffect } from "react";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import { DEFAULTPICKERDATE } from "@/types/Entities";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import PickerFormControlLabel from "@/components/tables/fields/PickerFormControlLabel";
import { requiredRule } from "@/components/tables/fields/fieldRules";
import { textFieldSx } from "@/components/tables/fields/fieldStyles";
import { useHideDateFieldForFauxOverdue } from "@/components/tables/fields/useHideDateFieldForFauxOverdue";
import {
  applyPickedDateTime,
  DATE_TIME_FORM_FORMAT,
  toDayjsOrNull,
} from "@/components/tables/fields/crudDateValues";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudDateTimeFieldProps {
  field: Pick<
    CrudModalField,
    "id" | "header" | "required" | "errorMessage" | "defaultDate"
  >;
  entityOfTable?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: string) => void;
}

const CrudDateTimeField = ({
  field,
  entityOfTable,
  validationTargetRef,
  onChange,
}: CrudDateTimeFieldProps) => {
  const name = field.id as string;
  const { control, getValues, setValue, trigger } = useFormContext();
  const hidden = useHideDateFieldForFauxOverdue(entityOfTable, field.id);

  useEffect(() => {
    if (!getValues(name)) {
      const now = new Date().toISOString();
      setValue(name, now);
      onChange?.(now);
    }
  }, []);

  if (hidden) return null;

  const fallbackForUnparsableInput =
    field.defaultDate === DEFAULTPICKERDATE.TODAY ? dayjs() : null;

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={requiredRule(field.required, field.errorMessage)}
      validationTargetRef={validationTargetRef}
      render={({
        field: { onChange: setFormValue, value, ...rest },
        error,
      }) => {
        const current = toDayjsOrNull(value);
        return (
          <PickerFormControlLabel
            header={field.header}
            required={field.required}
          >
            <GenericDateTime
              value={current}
              onChange={(picked) => {
                if (!picked) {
                  setFormValue("");
                  setValue(name, "");
                  trigger(name);
                  return;
                }
                if (!picked.isValid()) {
                  setFormValue(current || fallbackForUnparsableInput); 
                  return;
                }
                const formatted = applyPickedDateTime(current, picked).format(
                  DATE_TIME_FORM_FORMAT
                );
                setFormValue(formatted);
                onChange?.(formatted);
              }}
              textFieldProps={{
                name,
                onBlur: rest.onBlur,
                inputRef: rest.ref,
                error: !!error,
                sx: textFieldSx,
              }}
              className={styles.dateTimePicker}
            />
          </PickerFormControlLabel>
        );
      }}
    />
  );
};

export default CrudDateTimeField;
